import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { Device } from '../../../../model/device';
import { DevicesService } from '../../../../services/devices.service';
import { DeviceMeasurement } from '../../../../model/device-measurement';
import { MeasuresService } from '../../../../services/measures.service';
import { MapService } from '../../../../services/map.service';
import * as mapboxgl from 'mapbox-gl';
import { translate, TranslocoService } from '@ngneat/transloco';
import { TimezoneService } from '../../../../services/timezone.service';
import {ConversorsService} from "../../../../services/conversors.service";
import {GenericGraphComponent} from "../../../shared/generic-graph/generic-graph.component";

type ModeOption = {label: string, value: 'chart' | 'table'};
type MeasurementUnit = {origin: string, target: string, name: string};
type SelectMeasurementUnit = {origin: string, target: string, name: string};
type SelectItemGr = {measure: string, label: string, value: string, items: MeasurementUnit[]};

@Component({
  selector: 'app-device-measures',
  templateUrl: './device-measures.component.html',
  styleUrls: ['./device-measures.component.scss']
})
export class DeviceMeasuresComponent implements OnInit, OnDestroy {

  public readonly DEFAULT_ZOOM = 14;

  @Input()
  public device: Device;

  @ViewChild('measuresChart')
  public measuresChart: GenericGraphComponent;

  public deviceMeasurements: DeviceMeasurement[];
  public selectedMeasurements: any[];

  public rangeDates: Date[];
  public tableMeasures: any;
  public graphMeasures: any;
  public mapMeasures: any;
  public graphAxis: any[];
  public totalMeasures: number;

  public map: mapboxgl.Map;

  public noMapMeasures: boolean;
  public loadingMeasures = false;

  public modeOptions: ModeOption[];
  public selectedModeOption: ModeOption;

  public selectedMeasurementsUnit: SelectMeasurementUnit[];
  public selectedMeasurementUnit: MeasurementUnit;
  public measurementUnit: boolean;
  public groupedMeasurementUnit: SelectItemGr[];

  public actionsMeasure: any[];
  public displayActionsDialog: boolean;
  public exportVisible: boolean;

  public convert = this.conversorsService.convert;
  public rows = 10;
  public first = 0;
  public exportChart;
  public typeExport: 'XLSX' | 'CSV';

  constructor(private mapService: MapService,
              private devicesService: DevicesService,
              private translocoService: TranslocoService,
              private timezoneService: TimezoneService,
              private measuresService: MeasuresService,
              private conversorsService: ConversorsService) {
    const lastWeek = new Date();
    lastWeek.setHours(lastWeek.getHours() - 24 * 7);
    this.rangeDates = [lastWeek, new Date()];
    this.buildModeOptions();
    this.actionsMeasure = [];
    this.selectedMeasurementsUnit = [];
  }

  ngOnInit(): void {
    this.loadMeasurements();
    this.translocoService.selectTranslate('common.new').subscribe(() => {
      this.buildModeOptions()
      this.actionsMeasure = [
        {label: translate('model.conversion'), icon: 'pi pi-cog', command: () => this.showConversions()},
        {label: translate('common.exportXls'), icon: 'pi pi-file-excel', command:() => this.showExportToXls()},
        {label: translate('common.exportCsv'), icon: 'pi pi-file-export', command:() => this.showExportToCsv()},
        {label: translate('common.downloadPng'), icon: 'pi pi-download', command: () => this.downloadChart()}
      ];
    });
  }

  showConversions(): void {
    this.displayActionsDialog = true;
    this.measuresChart.resize();
  }

  hideConversions(): void {
    this.displayActionsDialog = false;
    this.measuresChart.resize();
  }

  showExportToXls(): void {
    this.exportVisible = true;
    this.typeExport = 'XLSX';
    this.selectedModeOption = this.modeOptions[1];
  }

  showExportToCsv(): void {
    this.exportVisible = true;
    this.typeExport = 'CSV';
    this.selectedModeOption = this.modeOptions[1];
  }

  downloadChart(): void {
    if (this.selectedModeOption.value === 'table') {
      this.selectedModeOption = this.modeOptions[0];
    }
    try {
      setTimeout(() => {
        if (this.measuresChart.chart.axes != undefined) {
          this.exportChart = this.measuresChart.chart;
        }
        this.measuresChart.download(this.exportChart);
      });
    } catch (ignored) {}
  }

  private buildModeOptions(): void {
    this.modeOptions = [
      {label: translate('misc.mode.chart'), value: 'chart'},
      {label: translate('misc.mode.table'), value: 'table'}
    ];
    this.selectedModeOption = this.modeOptions[0];
  }

  private loadMeasurements(): void {
    this.devicesService.getDeviceMeasurementsOfDevice(this.device.uuid).subscribe(response => {
      response.deviceMeasurements.forEach(measurement => {
        measurement.nameDesc =  measurement.name + measurement.alias +  measurement.description;
        if (measurement.description !== undefined && measurement.description.trim() !== '') {
          measurement.name = measurement.description;
        } else if (measurement.alias !== undefined && measurement.alias.trim() !== '') {
          measurement.name = measurement.alias;
        }
      });
      this.deviceMeasurements = response.deviceMeasurements
        .sort((a, b) => a.name > b.name ? 1 : -1);
    });
  }

  private loadMeasurementUnits(): void {
    const units = this.convert().possibilities();
    this.groupedMeasurementUnit = [];
    this.measurementUnit = false;
    this.deviceMeasurements.forEach(measurement => {
      if (this.selectedMeasurements.includes(measurement.uuid)) {
        if (measurement.measurementType.unit) {
          const originUnit = units.find(measure => measure === measurement.measurementType.unit);
          const items: MeasurementUnit[] = [];

          this.convert().from(originUnit).possibilities().forEach(poss => {
            items.push({
              origin: originUnit,
              target: poss,
              name: `${poss} (${this.convert().describe(poss).plural})`
            });
          });
          this.groupedMeasurementUnit.push({
            measure: measurement.name,
            label: originUnit,
            value: originUnit,
            items: items
          });
          this.selectedMeasurementsUnit[measurement.name] = {
            origin: originUnit,
            target: originUnit,
            name: `${originUnit} (${this.convert().describe(originUnit).plural})`
          };
        }
      }
    });
  }

  searchMeasures(): void {
    if (!this.selectedMeasurements || this.selectedMeasurements.length === 0) { return; }

    this.loadingMeasures = true;
    const utcRange = this.timezoneService.timestampRangeFromCalendarData(this.rangeDates);

    this.totalMeasures = 0;
    const totalGraphMeasures = [];
    const totalTableMeasures = [];
    let totalMapMeasures;
    const suffixes = [];
    const promises = [];
    this.deviceMeasurements.forEach(measurement => {
      if (this.selectedMeasurements.includes(measurement.uuid)) {
        this.selectedMeasurementUnit = null;
        if (this.selectedMeasurementsUnit[measurement.name] != undefined) {
          const target = this.selectedMeasurementsUnit[measurement.name].target;
          this.selectedMeasurementUnit = {
            target: target,
            origin: this.selectedMeasurementsUnit[measurement.name].origin,
            name: `${target} (${this.convert().describe(target).plural})`
          };
        }

        const unitTarget = ( this.selectedMeasurementUnit && this.selectedMeasurementUnit.origin == measurement.measurementType.unit ? this.selectedMeasurementUnit.target : measurement.measurementType.unit ?? '')
        const unitOrigin = measurement.measurementType.unit;

        const suffix = ' ' + unitTarget;
        let axisIndex;
        if (!suffixes.includes(suffix)) {
          suffixes.push(suffix);
        }
        axisIndex = suffixes.indexOf(suffix);

        const promise = this.measuresService.searchDeviceMeasures(
          this.device.tenant.uuid, this.device.uuid, measurement.uuid, utcRange.from, utcRange.until)
          .toPromise().then(response => {
            if (response.measures && response.measures.length >= 0) {
              if (measurement.measurementType.sensor === 'coordinate') {
                totalMapMeasures = this.buildSensorMapMeasures(response.measures);
                response.measures.forEach(measure => {
                  measure.measurementName = measurement.name;
                  measure.unit = unitTarget;
                  totalTableMeasures.push(measure);
                });
              } else {
                totalGraphMeasures.push({
                  name: measurement.name,
                  data: this.measuresService.buildSensorMeasures(response.measures, unitOrigin, unitTarget),
                  yAxis: axisIndex,
                  tooltip: {valueSuffix: suffix}
                });
                response.measures.forEach(measure => {
                  measure.measurementName = measurement.name;
                  measure.unit = unitTarget;
                  measure.value = this.selectedMeasurementUnit && measure.unit != measurement.measurementType.unit ? this.measuresService.convertValue(parseFloat(measure.value), unitOrigin, unitTarget) : measure.value;
                  totalTableMeasures.push(measure);
                });
              }
            }
          });

        promises.push(promise);

      }
    });

    Promise.all(promises).then(() => {
      this.graphMeasures = undefined;
      this.tableMeasures = undefined;
      this.totalMeasures = totalTableMeasures.length;
      this.graphAxis = suffixes.map(suffix => ( { labels: { format: '{value}' + suffix }, title: { enabled: false } } ) );
      setTimeout(() => this.graphMeasures = (totalGraphMeasures.length > 0 ) ? totalGraphMeasures : undefined, 0);
      setTimeout(() => this.tableMeasures = (totalTableMeasures.length > 0 ) ? totalTableMeasures : undefined, 0);
      this.mapMeasures = totalMapMeasures;
      this.buildAndConfigureMap(this.mapMeasures);
    }).finally(() => this.loadingMeasures = false);
  }

  clearConversions(): void {
    this.selectedMeasurementsUnit = [];
    this.searchMeasures();
    this.loadMeasurementUnits();
    this.displayActionsDialog = false;
  }

  private buildSensorMapMeasures(rawData: any[]): any[] {
    rawData.sort( (a, b) => (a.timestamp > b.timestamp) ? 1 : -1);
    const mapMeasures = [];
    for (const measure of rawData) {
      mapMeasures.push([parseFloat(measure.value.split(',')[0]), parseFloat(measure.value.split(',')[1])]);
    }
    return mapMeasures;
  }

  buildAndConfigureMap(mapMeasures: any[]): void {
    if (mapMeasures !== undefined) {
      if (mapMeasures.length === 0) {
        this.noMapMeasures = true;
      } else {
        setTimeout( () => {
          const lastLatitude = mapMeasures[mapMeasures.length - 1][1];
          const lastLongitude = mapMeasures[mapMeasures.length - 1][0];
          this.map = this.mapService.buildMap(this.DEFAULT_ZOOM, lastLatitude, lastLongitude);

          this.mapService.buildStaticPulsingDot(this.map, lastLatitude, lastLongitude);
          this.mapService.buildRoadMap(this.map, mapMeasures);
        }, 1000); // MAPBOX no consigue sacar el width del componente padre en el DOM, wait de 10 para que cargue...
      }
    }
  }


  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
    }
  }

}
